import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Extract session_id from URL fragment
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        console.error("No session_id in URL");
        navigate('/login');
        return;
      }

      const sessionId = sessionIdMatch[1];
      console.log("Processing session_id:", sessionId.substring(0, 20) + "...");

      try {
        // Exchange session_id for session data from Emergent auth
        const response = await fetch(
          'https://demobackend.emergentagent.com/auth/v1/env/oauth/session-data',
          {
            headers: {
              'X-Session-ID': sessionId
            }
          }
        );

        if (!response.ok) {
          console.error("Failed to get session data from Emergent:", response.status);
          throw new Error('Failed to get session data');
        }

        const sessionData = await response.json();
        console.log("Got session data for:", sessionData.email);

        // Send to our backend to create/update user and set cookie
        // Use window.location.origin for proper auth redirects across environments
        const backendResponse = await fetch(
          `${window.location.origin}/api/auth/session`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(sessionData)
          }
        );

        if (!backendResponse.ok) {
          console.error("Failed to create session in backend:", backendResponse.status);
          throw new Error('Failed to create session');
        }

        const userData = await backendResponse.json();
        console.log("User created/updated:", userData.email);

        // Store session token in localStorage as backup
        localStorage.setItem('session_token', sessionData.session_token);
        
        // Update auth context
        setUser(userData);

        // Clear the hash from URL and navigate to dashboard
        window.history.replaceState(null, '', '/');
        navigate('/', { replace: true, state: { user: userData } });

      } catch (error) {
        console.error('Auth error:', error);
        navigate('/login');
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="w-8 h-8 border-2 border-profit border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
}
